import { CheckYesIcon, XNoIcon } from "@minervaproject/fds";

import { useCapability, useUserIdsWithCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { USER_PICKER_PROPERTIES, useUsersProperties } from "hooks/use_user_properties";
import { setUserVote } from "shared/reducers/classroom";
import { CapabilityType } from "shared/types";
import { FilterStrategyType } from "shared/utils/user_picker";
import { classroomSelectors, localSelectors } from "store";
import { pickUsersAndSetParticipantBarAndStage } from "utils/classroom";

const DIM_OPACITY = 0.35;

function VoteRollup({ canFilter }: { canFilter: boolean }) {
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const canParticipateInClass = useCapability(CapabilityType.CAN_PARTICIPATE_IN_CLASS);
  const canParticipateInClassUserIds = useUserIdsWithCapability(
    CapabilityType.CAN_PARTICIPATE_IN_CLASS,
  );
  const users = Object.values(useUsersProperties(USER_PICKER_PROPERTIES.concat(["vote"])));
  const presentStudents = users.filter(
    ({ id, is_present }) => canParticipateInClassUserIds.includes(id) && is_present,
  );
  const yesCount = presentStudents.filter(({ vote }) => vote === "yes").length;
  const noCount = presentStudents.filter(({ vote }) => vote === "no").length;
  const totalCount = presentStudents.length;

  // For filtering
  const maxSubscribedStreams = useAppSelector(classroomSelectors.selectMaxSubscribedStreams());
  const isVisibleInClassUserIds = useUserIdsWithCapability(CapabilityType.IS_VISIBLE_IN_CLASS);
  const presentVisibleUsers = users.filter(
    (user) => user.is_present && isVisibleInClassUserIds.includes(user.id),
  );
  const { panes } = useAppSelector(classroomSelectors.selectStage());
  const isAlwaysSubscribedToUserIds = useUserIdsWithCapability(
    CapabilityType.IS_ALWAYS_SUBSCRIBED_TO,
  );
  const participantBarUserIds = useAppSelector(classroomSelectors.selectParticipantBarUserIds());

  const handleClickVote = (vote: "yes" | "no") => {
    if (canParticipateInClass) {
      dispatch(setUserVote({ userId: currentUserId, vote }));
    } else if (canFilter) {
      pickUsersAndSetParticipantBarAndStage(
        dispatch,
        maxSubscribedStreams,
        currentUserId,
        presentVisibleUsers,
        isAlwaysSubscribedToUserIds,
        canParticipateInClassUserIds,
        participantBarUserIds,
        panes,
        vote === "yes" ? FilterStrategyType.VotingYes : FilterStrategyType.VotingNo,
      );
    }
  };

  return (
    <div className="pt2 px2">
      <div className="flex items-center mb1">
        {canParticipateInClass || canFilter ? (
          <button
            className="button-reset cursor-pointer flex"
            type="button"
            aria-label={canParticipateInClass ? "Vote yes" : "Filter yes"}
            onClick={() => handleClickVote("yes")}
          >
            <CheckYesIcon
              width={16}
              height={16}
              style={{ opacity: yesCount > 0 ? 1 : DIM_OPACITY }}
            />
          </button>
        ) : (
          <span className="flex" data-testid="vote-rollup-yes-icon">
            <CheckYesIcon
              width={16}
              height={16}
              style={{ opacity: yesCount > 0 ? 1 : DIM_OPACITY }}
            />
          </span>
        )}
        <div className="mx2 rollup-bar flex-auto flex content-start">
          <div
            className="rollup-bar-content"
            style={{ width: `${(100 * yesCount) / totalCount}%` }}
          />
        </div>
        <span
          className="body-s text-black-tint-70 count"
          style={{ marginBottom: "2px" }}
          data-testid="yes-count"
        >
          {yesCount}
        </span>
      </div>
      <div className="flex items-center">
        {canParticipateInClass || canFilter ? (
          <button
            className="button-reset cursor-pointer flex"
            type="button"
            aria-label={canParticipateInClass ? "Vote no" : "Filter no"}
            onClick={() => handleClickVote("no")}
          >
            <XNoIcon width={16} height={16} style={{ opacity: noCount > 0 ? 1 : DIM_OPACITY }} />
          </button>
        ) : (
          <span className="flex" data-testid="vote-rollup-no-icon">
            <XNoIcon width={16} height={16} style={{ opacity: noCount > 0 ? 1 : DIM_OPACITY }} />
          </span>
        )}
        <div className="mx2 rollup-bar flex-auto flex content-start">
          <div
            className="rollup-bar-content"
            style={{ width: `${(100 * noCount) / totalCount}%` }}
          />
        </div>
        <span
          className="body-s text-black-tint-70 count"
          style={{ marginBottom: "2px" }}
          data-testid="no-count"
        >
          {noCount}
        </span>
      </div>
    </div>
  );
}

export { VoteRollup };
